import { useMemo } from 'react'
import { Client, createClient, createConfig } from '@/api/navigationApi/client'
import { useAuthStore } from '@/stores/auth'
import { controlUnitClient } from '@/api/clientInitializer'
import { getControlUnitApiUrl, getNavigationApiUrl } from '@/utils/navigation'

const useVehicleSpecificClient = (
  vehicleId: string | null | undefined,
  serviceName: 'navigation' | 'controlUnit',
  fallbackClient: Client
) => {
  return useMemo(() => {
    if (!vehicleId) {
      return fallbackClient
    }

    if (!fallbackClient) {
      console.warn(`${serviceName} client not available`)
      return undefined
    }

    const vehicleClient = createClient(
      createConfig({
        baseUrl:
          import.meta.env.VITE_VEHICLE_PROXY_URL ||
          (typeof window !== 'undefined' && window.ENV?.VEHICLE_PROXY_URL) ||
          '',
      })
    )

    vehicleClient.interceptors.request.use(config => {
      const authHeaders = useAuthStore.getState().getAuthHeaders()

      config.headers.set('city-sense-vehicle', vehicleId)
      config.headers.set('city-sense-service', serviceName)

      Object.entries(authHeaders).forEach(([key, value]) => {
        config.headers.set(key, value)
      })

      return config
    })

    return vehicleClient
  }, [vehicleId, serviceName, fallbackClient])
}

const useUnproxiedNavigationClient = () => {
  return useMemo(() => {
    const client = createClient(
      createConfig({
        baseUrl: getNavigationApiUrl(),
      })
    )

    return client
  }, [])
}

/**
 * Hook to create a vehicle-specific client for direct API calls
 * Use this when you need to make direct API calls with vehicle context
 */
export const useNavigationClient = (vehicleId: string | undefined) => {
  const unproxiedClient = useUnproxiedNavigationClient()
  const client = useVehicleSpecificClient(vehicleId, 'navigation', unproxiedClient)
  return client
}

export const useControlUnitClient = (vehicleId: string | null) =>
  useVehicleSpecificClient(vehicleId, 'controlUnit', controlUnitClient)

/**
 * Control unit client for the navigation screen: goes through the vehicle proxy
 * when a vehicleId is present, otherwise talks to the in-car control unit directly
 * (mirrors how useNavigationClient resolves the local navigation API)
 */
export const useLocalOrProxiedControlUnitClient = (vehicleId: string | null) => {
  const localClient = useMemo(() => {
    return createClient(
      createConfig({
        baseUrl: getControlUnitApiUrl(),
      })
    )
  }, [])

  return useVehicleSpecificClient(vehicleId, 'controlUnit', localClient)
}
